import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBillingConfig, BillingConfig } from "@/hooks/useBillingConfig";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Save, CreditCard, Building2, FileText, Link2, Plus, Clock, Shield, AlertTriangle, Trash2, MoreVertical, EyeOff, Eye, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { createElement } from "react";
import { PAYMENT_ICON_MAP } from "@/lib/paymentIcons";

export default function SettingsBilling() {
  const { t } = useTranslation("admin");
  const { config, isLoading, updateConfig } = useBillingConfig();
  const {
    visibleMethods,
    hiddenMethods,
    isLoading: methodsLoading,
    updateMethod,
    createMethod,
    deleteMethod,
  } = usePaymentMethods();
  const [showHidden, setShowHidden] = useState(false);
  const [form, setForm] = useState<Partial<BillingConfig>>({});
  const [newMethodName, setNewMethodName] = useState("");

  useEffect(() => {
    if (config) setForm({});
  }, [config]);

  const current = { ...config, ...form } as BillingConfig;

  const update = (field: string, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (Object.keys(form).length === 0) return;
    updateConfig.mutate(form, {
      onSuccess: () => toast.success(t("settings.billing.successSaved")),
      onError: (err) => toast.error(err.message || t("settings.billing.errorCouldNotSave")),
    });
  };

  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Payment Methods */}
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <CreditCard className="h-4 w-4" /> {t("settings.billing.sectionPaymentMethods")}
        </h3>
        {methodsLoading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {visibleMethods.map((method) => {
              const IconComp = method.icon ? PAYMENT_ICON_MAP[method.icon] : null;
              return (
                <div key={method.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    {IconComp && createElement(IconComp, { className: "h-4 w-4 text-muted-foreground" })}
                    <span className="text-sm font-medium">{method.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={method.enabled}
                      onCheckedChange={(v) => {
                        updateMethod.mutate({ id: method.id, updates: { enabled: v } });
                      }}
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7">
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => updateMethod.mutate({ id: method.id, updates: { is_hidden: true } })}>
                          <EyeOff className="h-3.5 w-3.5 mr-2" />
                          {t("settings.billing.hide")}
                        </DropdownMenuItem>
                        {!method.is_system && (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              if (confirm(t("settings.billing.confirmDelete", { name: method.name }))) {
                                deleteMethod.mutate(method.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            {t("settings.billing.delete")}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hidden payment methods */}
          {hiddenMethods.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <button
                onClick={() => setShowHidden(!showHidden)}
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showHidden && "rotate-180")} />
                {t("settings.billing.hiddenMethods", { count: hiddenMethods.length })}
              </button>
              {showHidden && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {hiddenMethods.map((method) => {
                    const IconComp = method.icon ? PAYMENT_ICON_MAP[method.icon] : null;
                    return (
                      <div key={method.id} className="flex items-center justify-between p-3 border border-dashed rounded-lg opacity-60">
                        <div className="flex items-center gap-2">
                          {IconComp && createElement(IconComp, { className: "h-4 w-4 text-muted-foreground" })}
                          <span className="text-sm">{method.name}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateMethod.mutate({ id: method.id, updates: { is_hidden: false } })}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {t("settings.billing.unhide")}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>)}

        {/* Add custom payment method */}
        <div className="mt-4 flex items-center gap-2">
          <Input
            value={newMethodName}
            onChange={(e) => setNewMethodName(e.target.value)}
            placeholder={t("settings.billing.placeholderMethodName")}
            className="h-8 text-sm max-w-xs"
          />
          <Button
            variant="outline"
            size="sm"
            className="gap-1 shrink-0"
            disabled={!newMethodName.trim() || createMethod.isPending}
            onClick={() => {
              const name = newMethodName.trim();
              if (!name) return;
              const key = name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
              createMethod.mutate(
                { key, name },
                {
                  onSuccess: () => {
                    setNewMethodName("");
                    toast.success(t("settings.billing.methodAdded"));
                  },
                  onError: (err) => toast.error(err.message),
                },
              );
            }}
          >
            <Plus className="h-3.5 w-3.5" /> {t("settings.billing.btnAddMethod")}
          </Button>
        </div>
      </div>

      <Separator />

      {/* Company Info */}
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <Building2 className="h-4 w-4" /> {t("settings.billing.sectionCompanyInfo")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("settings.billing.fieldCompanyName")}</Label>
            <Input value={current.company_name ?? ""} onChange={(e) => update("company_name", e.target.value)} className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">{t("settings.billing.fieldOrgNumber")}</Label>
            <Input value={current.company_org_number ?? ""} onChange={(e) => update("company_org_number", e.target.value)} className="h-9 text-sm" placeholder="XXXXXX-XXXX" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">{t("settings.billing.fieldAddress")}</Label>
            <Input value={current.company_address ?? ""} onChange={(e) => update("company_address", e.target.value)} className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">{t("settings.billing.fieldEmail")}</Label>
            <Input value={current.company_email ?? ""} onChange={(e) => update("company_email", e.target.value)} className="h-9 text-sm" type="email" />
          </div>
          <div>
            <Label className="text-xs">{t("settings.billing.fieldPhone")}</Label>
            <Input value={current.company_phone ?? ""} onChange={(e) => update("company_phone", e.target.value)} className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">{t("settings.billing.fieldBankgiroInvoice")}</Label>
            <Input value={current.company_bankgiro ?? ""} onChange={(e) => update("company_bankgiro", e.target.value)} className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">{t("settings.billing.fieldSwishInvoice")}</Label>
            <Input value={current.company_swish ?? ""} onChange={(e) => update("company_swish", e.target.value)} className="h-9 text-sm" />
          </div>
        </div>
      </div>

      <Separator />

      {/* Invoice Settings */}
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4" /> {t("settings.billing.sectionInvoiceSettings")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("settings.billing.fieldPaymentTerms")}</Label>
            <Select value={String(current.default_payment_terms_days)} onValueChange={(v) => update("default_payment_terms_days", Number(v))}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">{t("settings.billing.days", { count: 10 })}</SelectItem>
                <SelectItem value="15">{t("settings.billing.days", { count: 15 })}</SelectItem>
                <SelectItem value="20">{t("settings.billing.days", { count: 20 })}</SelectItem>
                <SelectItem value="30">{t("settings.billing.days", { count: 30 })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t("settings.billing.fieldDefaultVatRate")}</Label>
            <Select value={String(current.default_vat_rate)} onValueChange={(v) => update("default_vat_rate", Number(v))}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0%</SelectItem>
                <SelectItem value="6">{t("settings.billing.vat6")}</SelectItem>
                <SelectItem value="12">{t("settings.billing.vat12")}</SelectItem>
                <SelectItem value="25">{t("settings.billing.vat25")}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">{t("settings.billing.vatHint")}</p>
          </div>
        </div>
      </div>

      <Separator />

      {/* Payment Policy */}
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4" /> {t("settings.billing.sectionPaymentPolicy")}
        </h3>
        <div className="space-y-4">
          {/* Deadline */}
          <div className="space-y-2">
            <Label className="text-xs">{t("settings.billing.fieldPaymentDeadline")}</Label>
            <div className="flex items-center gap-2">
              <Select
                value={current.payment_deadline_mode ?? "none"}
                onValueChange={(v) => update("payment_deadline_mode", v)}
              >
                <SelectTrigger className="h-9 text-sm w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("settings.billing.deadlineNone")}</SelectItem>
                  <SelectItem value="before_start">{t("settings.billing.deadlineBeforeStart")}</SelectItem>
                  <SelectItem value="after_booking">{t("settings.billing.deadlineAfterBooking")}</SelectItem>
                </SelectContent>
              </Select>
              {current.payment_deadline_mode !== "none" && (
                <>
                  <NumberInput
                    min={0}
                    max={168}
                    className="h-9 text-sm w-20"
                    value={current.payment_deadline_hours ?? 2}
                    onValueChange={(v) => update("payment_deadline_hours", v || 0)}
                  />
                  <span className="text-sm text-muted-foreground">{t("settings.billing.hours")}</span>
                </>
              )}
            </div>
          </div>

          {/* Sprint R1: "Require full payment" toggle removed. The rule is
              now unconditional — every paid-price online booking starts in
              `status='reserved'` and is promoted to `confirmed` only when
              the booker's obligation is settled (see
              `booking_booker_obligation_settled` and
              `src/lib/bookingConfirmation.ts`). The DB column
              `billing_config.require_full_payment_at_booking` is left in
              place (deprecated, no read-sites) so historical writes are
              not destructive; it can be dropped in a later cleanup. */}

          {/* Reminders */}
          {current.payment_deadline_mode !== "none" && (
            <div>
              <Label className="text-xs">{t("settings.billing.fieldReminders")}</Label>
              <Input
                className="h-9 text-sm max-w-xs"
                value={current.payment_reminder_hours ?? "24,6,1"}
                onChange={(e) => update("payment_reminder_hours", e.target.value)}
                placeholder="24,6,1"
              />
            </div>
          )}

          <Separator />

          {/* Guarantor */}
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> {t("settings.billing.autoCharge")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.billing.autoChargeDesc")}</p>
              </div>
              <Switch
                checked={current.guarantor_charge_enabled ?? false}
                onCheckedChange={(v) => update("guarantor_charge_enabled", v)}
              />
            </div>

            {/* Grace period */}
            <div>
              <Label className="text-xs">{t("settings.billing.fieldGracePeriod")}</Label>
              <div className="flex items-center gap-2">
                <NumberInput
                  min={0}
                  max={48}
                  className="h-9 text-sm w-20"
                  value={current.grace_period_hours ?? 2}
                  onValueChange={(v) => update("grace_period_hours", v || 0)}
                />
                <span className="text-sm text-muted-foreground">{t("settings.billing.hours")}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{t("settings.billing.gracePeriodHint")}</p>
            </div>
          </div>

          <Separator />

          {/* Auto-cancel */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {t("settings.billing.autoCancel")}</p>
              <p className="text-xs text-muted-foreground">{t("settings.billing.autoCancelDesc")}</p>
            </div>
            <Switch
              checked={current.auto_cancel_unpaid ?? false}
              onCheckedChange={(v) => update("auto_cancel_unpaid", v)}
            />
          </div>

          {/* Preview */}
          {current.payment_deadline_mode !== "none" && (
            <Alert className="bg-muted/50">
              <Clock className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {(() => {
                  const mode = current.payment_deadline_mode;
                  const hours = current.payment_deadline_hours ?? 2;
                  const grace = current.grace_period_hours ?? 2;
                  const guarantor = current.guarantor_charge_enabled;
                  const autoCancel = current.auto_cancel_unpaid;

                  let text = `Spelare bokar ons 18:00. `;
                  if (mode === "before_start") {
                    text += `Medspelare har till ${hours}h före (ons ${18 - hours < 0 ? `tis ${24 + 18 - hours}` : `${18 - hours}`}:00) att betala. `;
                  } else {
                    text += `Medspelare har ${hours}h efter bokningstillfället att betala. `;
                  }
                  if (guarantor) text += `Om ej betalt debiteras bokningsansvarigs kort. `;
                  if (guarantor) text += `Misslyckas det har spelaren ${grace}h till. `;
                  else text += `Därefter har spelaren ${grace}h grace period. `;
                  if (autoCancel) text += `Därefter avbokas tiden automatiskt.`;
                  else text += `Obetalda hanteras manuellt av admin.`;
                  return text;
                })()}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <Separator />

      {/* Fortnox */}
      <div className="opacity-60">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <Link2 className="h-4 w-4" /> {t("settings.billing.sectionFortnox")}
          <Badge variant="secondary" className="text-[10px]">{t("settings.billing.comingSoon")}</Badge>
        </h3>
        <p className="text-xs text-muted-foreground mb-2">
          {t("settings.billing.fortnoxDesc")}
        </p>
        <Button variant="outline" size="sm" disabled>{t("settings.billing.btnConnectFortnox")}</Button>
      </div>

      <Separator />

      <Button onClick={handleSave} disabled={updateConfig.isPending || Object.keys(form).length === 0}>
        {updateConfig.isPending ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
        {t("settings.billing.btnSave")}
      </Button>
    </div>
  );
}
